// NATIVE
import React, { useMemo, useState, useEffect } from 'react';

// CUSTOM
import { Avatar, SxProps, Theme } from '@mui/material';
import { getUrlBase } from '../commons/utils/helpers';

export interface AvatarTablesProps {
  urlImage?: string | null;
  typeImage: string;
  tenantId?: string;
  size?: 'cards' | 'tables' | 'avatar' | string;
  style?: React.CSSProperties;
  variant?: 'circular' | 'rounded' | 'square';
  sx?: SxProps<Theme>;
  alt?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down';
  children?: React.ReactNode;
}

export const AvatarTables = React.memo(
  ({
    urlImage,
    typeImage,
    tenantId: tenantIdProp,
    size,
    style,
    variant = 'square',
    sx = { width: 90, height: 90 },
    alt = 'Logo Compañia',
    objectFit = 'cover',
    children,
  }: AvatarTablesProps) => {
    const [imageError, setImageError] = useState(false);
    const [triedFallback, setTriedFallback] = useState(false);

    const { directUrl, fallbackUrl } = useMemo(() => {
      if (!urlImage || urlImage === 'Cargando...' || urlImage === 'loading') {
        return { directUrl: '', fallbackUrl: '' };
      }

      const img = urlImage.trim();
      if (!img) return { directUrl: '', fallbackUrl: '' };

      if (
        img.startsWith('http://') ||
        img.startsWith('https://') ||
        img.startsWith('blob:') ||
        img.startsWith('data:')
      ) {
        return { directUrl: img, fallbackUrl: '' };
      }

      if (img.includes('/v1/commons/storage/')) {
        const storagePath = img.startsWith('/') ? img : `/${img}`;
        return { directUrl: `${getUrlBase()}${storagePath}`, fallbackUrl: '' };
      }

      let cleanName = img.split('?')[0];
      if (cleanName.includes('/')) {
        cleanName = cleanName.split('/').pop() || '';
      }

      let finalTenantId = tenantIdProp || '';

      if (!finalTenantId) {
        try {
          const userRaw =
            window.localStorage.getItem('@iyerghHGkkjd_CYO_user') ||
            window.localStorage.getItem('@QJ45w9vZsed_storage_User');

          if (userRaw) {
            const user = JSON.parse(userRaw);
            finalTenantId =
              user?.tenantId ||
              user?.tenant_id ||
              user?.cliente?.tenantId ||
              user?.cliente?.tenant ||
              user?.idTenant ||
              user?.tenant?.id ||
              user?.tenant ||
              '';
          }
        } catch {
          // Ignorar error de localStorage
        }
      }

      if (!finalTenantId) {
        try {
          finalTenantId =
            window.localStorage.getItem('tenantId') ||
            window.localStorage.getItem('@tenantId') ||
            '';
        } catch {
          // Ignorar error de localStorage
        }
      }

      const queryParams: string[] = [];
      if (finalTenantId) {
        queryParams.push(`tenantId=${encodeURIComponent(finalTenantId)}`);
      }
      if (size) {
        queryParams.push(`size=${encodeURIComponent(size)}`);
      }
      const query = queryParams.length ? `?${queryParams.join('&')}` : '';

      return {
        directUrl: `${getUrlBase()}/v1/files/${typeImage}/${cleanName}${query}`,
        fallbackUrl: `${getUrlBase()}/v1/inventario-pos/pos-users/${typeImage}/${cleanName}${query}`,
      };
    }, [urlImage, typeImage, tenantIdProp, size]);

    // Restablecer el estado de error cuando cambia la URL
    useEffect(() => {
      setImageError(false);
      setTriedFallback(false);
    }, [directUrl]);

    const activeSrc = !triedFallback ? directUrl : fallbackUrl;

    const handleImgError = () => {
      if (!triedFallback && fallbackUrl && fallbackUrl !== directUrl) {
        setTriedFallback(true);
      } else {
        setImageError(true);
      }
    };

    const isSquare = variant === 'square';

    return (
      <Avatar
        sx={[
          {
            bgcolor: '#e2e8f0',
            color: '#64748b',
            borderRadius: isSquare ? 0 : 1,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        alt={alt}
        src={activeSrc && !imageError ? activeSrc : undefined}
        variant={variant}
        style={style}
        slotProps={{
          img: {
            loading: 'lazy',
            decoding: 'async',
            style: {
              objectFit,
              width: '100%',
              height: '100%',
              borderRadius: isSquare ? 0 : undefined,
            },
            onError: handleImgError,
          },
        }}
      >
        {children || alt?.charAt(0).toUpperCase() || 'Q'}
      </Avatar>
    );
  }
);

AvatarTables.displayName = 'AvatarTables';
